import { useCallback, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { DEFAULT_LAYOUT_NAME } from '@core';
import { Layout } from '@data';
import { useUpsertLayout } from '@queries';
import { ActionButton, colors, Input, Spinner } from '@ui';

type NameForm = { name: string };

type LayoutNameInputProps = {
    isEditing?: boolean;
    layout: Layout;
    onCancelEdit: () => void;
};

export const LayoutNameInput = ({ isEditing, layout, onCancelEdit }: LayoutNameInputProps) => {
    const { mutateAsync: updateLayout, isPending, isError } = useUpsertLayout();

    const { register, handleSubmit } = useForm<NameForm>({
        defaultValues: {
            name: layout.name || DEFAULT_LAYOUT_NAME,
        },
    });
    useEffect(() => {
        if (isError) {
            toast.error('Failed to update the layout name.');
        }
    }, [isError]);
    const onSave = useCallback(
        async (data: NameForm) => {
            await updateLayout({
                ...layout,
                name: data.name,
            });
            onCancelEdit();
        },
        [layout, updateLayout, onCancelEdit],
    );
    if (!isEditing) {
        return <span>{layout.name || DEFAULT_LAYOUT_NAME}</span>;
    }

    return (
        <form
            onSubmit={handleSubmit(onSave)}
            onClick={(evt) => {
                isEditing && evt.stopPropagation();
            }}
            style={{ pointerEvents: !isEditing ? 'none' : 'unset' }}
        >
            <Input
                style={{ flex: 1 }}
                {...register('name', { minLength: 4, required: true })}
                font="h3"
                disabled={!isEditing}
                variant="plain"
                overlay={<Overlay isEditing={isEditing} onCancel={onCancelEdit} isPending={isPending} />}
            />
        </form>
    );
};

type OverlayProps = {
    isEditing?: boolean;
    onCancel: () => void;
    isPending?: boolean;
};

const Overlay = ({ isEditing, onCancel, isPending }: OverlayProps) => {
    if (isEditing) {
        return (
            <>
                <ActionButton icon="close" variant="plain" iconColor={colors.red[500]} onClick={onCancel} />
                <ActionButton icon="checkmark" variant="plain" type="submit" />
            </>
        );
    }
    if (isPending) {
        return <Spinner size="s" />;
    }
    return null;
};
